/* eslint-disable lit/no-invalid-html */
/* eslint-disable lit/binding-positions */
import { nothing, unsafeCSS } from 'lit';
import { html, literal } from 'lit/static-html.js';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import {
  BaseElement,
  customElement,
  SlotObserver,
} from '../../../components/base-element';
import '../../../components/text/text-subtitle.wc';
import '../../../components/text/text.wc';
import '../../../components/buttons/text-button/text-button.wc';
import '../../../components/stack/stack.wc';
import styles from './resource-card.scss?inline';

const descTextMaxLines = 2;

@customElement('ps-resource-card')
export class ResourceCardWC extends SlotObserver(BaseElement, [
  '[slot="action"]',
  '[slot="hover-action"]',
]) {
  static styles = unsafeCSS(styles);

  private resizeObserver: ResizeObserver;

  @query('.c-resource-card__description-text') descText: HTMLElement;

  /** Label content. */
  @property({
    reflect: true,
  })
  label: string;

  /** Description content. */
  @property({
    reflect: true,
  })
  description?: string;

  /** Set component variant. */
  @property({ reflect: true }) variant: 'default' | 'neutral' = 'default';

  /** If true, resource-card will be rendered as an `<button>` tag */
  @property({ type: Boolean, reflect: true }) interactive = false;

  /** If true, resource-card in selected state */
  @property({ type: Boolean, reflect: true }) selected = false;

  /** The resource-card's disabled state. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  @state() showMore = false;

  @state() isDescTextTruncated = false;

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver((entry) =>
      this.syncDescText(entry)
    );

    this.updateComplete.then(() => {
      if (this.descText) this.resizeObserver.observe(this.descText);
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.descText) this.resizeObserver.unobserve(this.descText);
  }

  syncDescText(entry: ResizeObserverEntry[]) {
    const elem = entry[0].target.shadowRoot?.querySelector('*');

    if (elem) {
      const isTextTruncated =
        elem.scrollHeight >
        parseInt(getComputedStyle(elem).lineHeight, 10) * descTextMaxLines;

      this.isDescTextTruncated = isTextTruncated;
    }
  }

  toggleDescriptionText = (e: MouseEvent) => {
    e.stopPropagation();
    this.showMore = !this.showMore;
  };

  render() {
    const tag = this.interactive ? literal`button` : literal`div`;

    const wrapperClasses = classMap({
      'c-resource-card': true,
      [`c-resource-card--variant-${this.variant}`]: this.variant,
      'c-resource-card--interactive': this.interactive,
      'c-resource-card--selected': this.selected,
    });

    const btnClasses = classMap({
      'c-resource-card__btn': true,
      'c-resource-card__btn--interactive': this.interactive,
      'c-resource-card__btn--selected': this.selected,
    });

    return html`
      <div class=${wrapperClasses}>
        <div class="c-resource-card__inner">
          <${tag} class=${btnClasses}>
            <ps-stack justifyContent="start" alignItems="center" gutter="xsmall" nowrap>
              <slot name="label-prefix"></slot>
              <ps-text-subtitle size="xsmall" truncate class="c-resource-card__title">${this.label}</ps-text-subtitle>
              <slot name="label-suffix"></slot>
            </ps-stack>
          </${tag}>
          ${
            this.description
              ? html`
                  <div class="c-resource-card__description">
                    <ps-text
                      size="3xsmall"
                      variant="secondary"
                      ?truncate=${!this.showMore}
                      .truncateLines=${this.showMore
                        ? undefined
                        : descTextMaxLines}
                      class="c-resource-card__description-text"
                    >
                      ${this.description}
                    </ps-text>
                    ${this.isDescTextTruncated
                      ? html`
                          <div class="c-resource-card__toggle-text-btn">
                            <ps-text-button
                              @click=${this.toggleDescriptionText}
                            >
                              Show ${this.showMore ? 'less' : 'more'}
                            </ps-text-button>
                          </div>
                        `
                      : nothing}
                  </div>
                `
              : nothing
          }
          
        </div>
        ${
          this.slotted('action')
            ? html`<span class="c-resource-card__action">
                <slot name="action"></slot>
              </span>`
            : nothing
        }
        ${
          this.slotted('hover-action')
            ? html`<span class="c-resource-card__hover-action">
                <slot name="hover-action"></slot>
              </span>`
            : nothing
        }
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-resource-card': ResourceCardWC;
  }
}
